/**
 * A host-realm app (RFC 0275 §3): a module, not an iframe.
 *
 * The sandboxed tier reaches the host over postMessage, which can carry JSON and
 * nothing else. A `DocHandle` is a live object graph over wasm state, so it can
 * never cross that boundary, and an app that cannot hold a handle cannot edit
 * collaboratively. That is the whole reason this tier exists.
 *
 * NOT PATCHWORK'S IMPORT MAP, deliberately. Patchwork externalises Automerge and
 * injects an import map so a tool's bare specifiers resolve to the host's copy.
 * That is right for them: their tools are ordinary packages authored to import
 * normally. Ours are not, and the import map costs a load-bearing externals list
 * whose failure mode is a SECOND Automerge instance and a wasm trap at the first
 * cross-instance call, plus a specifier that resolves to a hashed dev URL under
 * Vite and a bundled chunk in production.
 *
 * So the app imports nothing. It exports a factory and the host hands it what it
 * needs. Identity is guaranteed by construction rather than by a list: there is
 * no import to resolve, so a second instance cannot be obtained.
 *
 * The trade, stated because it is real: an app written this way is not an
 * ordinary npm package that runs outside Mod, and a Patchwork tool would not
 * drop in. For the editor we ship, that is worth it. Import-map resolution can
 * be added later for foreign modules; the factory does not foreclose it.
 */

import * as A from '@automerge/automerge'
import type { AnyDocumentId, DocHandle, Repo } from '@automerge/automerge-repo'
import { isValidDocumentId } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  answerComment,
  type Candidate,
  type CommentAuthor,
  type CommentEntry,
  type CommentTarget,
  type CommentThread,
  changeNote,
  createComment,
  type DocumentDoc,
  deleteComment,
  deliverMentions,
  type Mention,
  type ProjectDoc,
  projectAuthor,
  projectCandidates,
  projectComments,
  projectThreads,
  resolveComment,
  SessionTaskLog,
} from '@mod/core/model'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { HostAgentEdits } from '../../features/documents/agentEditsBridge'
import {
  DocumentEmbed,
  type LinkOpenTarget,
  useDocumentResolver,
} from '../../features/documents/DocumentLink'
import {
  type AppControlHandler,
  type AppControlManifest,
  type AppViewController,
  createLocalAppControl,
} from '../../services/apps/appControl'
import { watchComments } from '../../services/comments/commentWatch'
import {
  commentAuthor,
  editorAuthor,
} from '../../services/identity/commentAuthor'
import {
  type CommentReader,
  commentReader,
  type ThreadWaiting,
  threadWaiting,
} from '../../services/sessions/commentReader'
import { sessionFace } from '../../services/sessions/sessionFace'
import {
  routeComment,
  useSpaceSession,
} from '../../services/sessions/spaceSession'
import { holdDocument } from '../../services/world/repo/held-docs'
import { useOptionalTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { ensureCommentElements } from '../ui/commentElements'
import { ensureTaskElements } from '../ui/taskElements'
import type { AppTasks } from './tasks'

// Host-realm apps (including the shipped editor) can create the framework-free
// task elements directly. Register once before any app module is mounted.
ensureTaskElements()
// And the comment elements (RFC 0324 S8c): the shipped editor draws its margin
// cards with `<mod-comment-thread>`, and looks it up rather than importing it.
ensureCommentElements()

/**
 * The comments on the document being edited (RFC 0267 §4a), as an app sees them.
 *
 * The SAME calls the sandboxed bridge serves under `mod.comments.*`, minus
 * the target: a host-realm editor is mounted on one document and every comment
 * it can make is about that document, so the host fills the `documentId` in
 * rather than letting an app name one. What is left for the app to say is where
 * inside the document it means — which is the anchor, and only the app knows it.
 *
 * `subscribe` exists because these arrive on the PROJECT doc, which the app
 * holds no handle to: a comment written by an agent, or by someone on another
 * machine, has to reach an editor that is already mounted.
 */
/** A comment with its writer resolved (RFC 0324 3a), so an app draws a face
 *  without reading the project's people itself. */
export type AppComment = CommentEntry & { authorInfo: CommentAuthor }

export interface AppComments {
  list: () => AppComment[]
  /** The same comments grouped into exchanges (RFC 0289 §4). Beside `list`
   *  rather than replacing it: a count wants every comment, a column wants
   *  threads, and both get asked. */
  threads: () => CommentThread[]
  /**
   * The things an `@` in a comment can name, ranked (RFC 0292 §3c).
   *
   * The host ranks rather than the app, for the reason the bridge gives a
   * sandboxed view the same call: the answer is a projection over the project
   * document, which an editor mounted on ONE document has no handle to and no
   * business opening.
   */
  candidates: (prefix: string) => Candidate[]
  /** `anchor` carries the target's positional fields (RFC 0285's `hunk` and
   *  `anchorText`); the document is the one being edited. `mentions` is what the
   *  body names, and naming a session WAKES it (RFC 0292). */
  create: (
    anchor: Partial<CommentTarget>,
    body: string,
    mentions?: Mention[],
  ) => Promise<string>
  /**
   * Answer an existing comment (RFC 0289 §3) rather than adding a second one
   * beside it. No anchor argument: a reply takes its root's, so the host cannot
   * let an app put an answer on a different line from the question.
   *
   * Resolves with the session that was WOKEN, or null. When an agent wrote the
   * comment being answered its session is told and queued a turn, which is the
   * whole reason this is not `create` with a parent id.
   *
   * TWO EDGES, as everywhere else this exists: the reply wakes whoever wrote the
   * comment being answered, and the mentions wake whoever the reply names.
   */
  reply: (
    parentId: string,
    body: string,
    mentions?: Mention[],
  ) => Promise<{ id: string; notifiedSessionId: string | null }>
  /** Acts on the whole THREAD whichever id in it is named (RFC 0289 §3). */
  resolve: (commentId: string, resolved: boolean) => Promise<void>
  remove: (commentId: string) => Promise<void>
  /** RFC 0324 3c: who a reply to `replyTo` would reach, or a new comment when
   *  it is empty, said before it is sent. The line under a box. Takes the
   *  target first, in the element port's order. */
  reader?: (target: unknown, replyTo?: string) => CommentReader
  /** RFC 0324 3d: what the thread under `rootId` is waiting for. */
  pending?: (rootId: string) => ThreadWaiting | null
  subscribe: (fn: () => void) => () => void
}

/** What a host-realm app receives. The contract, in one place. */
export interface AppContext {
  /** Where to render. The host owns it and clears it on unmount. */
  container: HTMLElement
  /** The document being edited: the host's OWN handle, already syncing. For a
   *  file off a branch (RFC 0277 S8) it is a `snapshotHandle` instead: a
   *  document-shaped read of the git server's bytes that never changes. */
  handle: DocHandle<DocumentDoc>
  /** Visual diff for a code file. Its lines never enter the handle or a save. */
  fileDiff?: AppFileDiff
  /** The host's repo, for an app that needs to reach other documents. */
  repo: Repo
  /** The ONE Automerge instance. Calling into any other one fails. */
  automerge: typeof A
  /** The document this app was mounted on, so an app can name it in a write. */
  documentId: string
  /** Comments on that document, read and written through the host. Absent when
   *  the project catalog could not be opened: an app must treat commenting as
   *  something a host may not offer, rather than assume it. */
  comments?: AppComments
  /** Shared tasks, for editors that promote and render task links (RFC 0319). */
  tasks?: AppTasks
  /** RFC 0301 S7: the agent's edits to this document, and undo. An editor that
   *  ignores it is the editor that existed before agents could edit. */
  edits?: HostAgentEdits
  /** RFC 0313 3a: stamped on every change the person makes, naming them. An
   *  editor that ignores it still works; its changes are just unattributed. */
  changeMessage?: string
  /**
   * "I do my own commenting" — call it during mount, and the host takes ITS
   * commenting surface off this pane.
   *
   * The host lays a passage popover over every document pane, because most
   * editors know nothing about comments and a person still has to be able to say
   * something about a sentence. An editor that has its own therefore got two:
   * two Comment buttons on one selection, and a passage commented on in one of
   * them answered in a card that looked nothing like it.
   *
   * A CLAIM RATHER THAN A GUESS. The host cannot tell from the outside, and
   * being handed the comments interface proves nothing — every host-realm app
   * gets it. An app that says nothing keeps the popover, which is the behaviour
   * every app had before this existed.
   */
  claimComments?: () => void
  /**
   * Links to other documents in this project (RFC 0297 §5a).
   *
   * THE HOST RESOLVES, and that is the seam. An editor handed a repo and a
   * project id could read the catalog itself, and then it would have to know
   * what a catalog is, which app draws an instance, and how a display path
   * differs from an id. It asks instead, and stays a thing that edits text.
   *
   * Absent means the host offers none, which is the behaviour every editor had
   * before this: a document link is text like any other.
   */
  links?: AppLinks
  /**
   * Declare the commands this mounted view answers (RFC 0328 3d), the same
   * `register` a framed app finds at `mod.surface.control`. Absent on a host
   * that offers no `control_app`; an app that never calls it is refused plainly.
   */
  control?: {
    register: (
      manifest: AppControlManifest,
      handler: AppControlHandler,
    ) => () => void
  }
  /** Register teardown. Called before the container is cleared. */
  onDispose?: (fn: () => void) => void
}

export interface AppFileDiff {
  /** Null when Changed mode is off or the comparison is unavailable. */
  lines: (currentText: string) => Array<{
    kind: 'same' | 'added' | 'removed'
    text: string
    newLine: number | null
  }> | null
  subscribe: (fn: () => void) => () => void
}

export interface AppLinks {
  /** What an href names, or null for an ordinary URL. */
  resolve: (href: string) => AppLinkTarget | null
  /** A document ID outside the visible catalog, not an ordinary URL. */
  unavailable?: (href: string) => boolean
  /** Open it beside this one. */
  open: (id: string, target?: LinkOpenTarget) => void
  /**
   * Draw that document into an element the caller owns, and return its teardown.
   *
   * THE HOST DRAWS, for the same reason it resolves. Rendering a document means
   * an app view in a frame with a bridge, a grant set and a subject, which is
   * host machinery; an editor that reimplemented it would be an app host inside
   * an app. It is handed an element and hands back a disposer, which is the
   * smallest contract that works for a bundle with no React in it.
   */
  mount: (el: HTMLElement, id: string) => (() => void) | undefined
}

export interface AppLinkTarget {
  id: string
  title: string
  /**
   * Whether this one may be DRAWN here, as against merely named.
   *
   * Answered by the host because only the host knows what would happen: a
   * document that embedded itself would draw a second copy of the page it is
   * on. Carried on the resolution rather than asked separately, since the
   * caller has already made that call and a second question is a second thing
   * to keep in step.
   */
  embed: boolean
}

type AppModule = { default?: (ctx: AppContext) => void }

/**
 * A file off a branch, shaped like a document (RFC 0277 S8).
 *
 * A file in the repository is not a document (RFC 0281 §4), so there is no
 * handle to find. What the app needs of one is `doc()`, and for a read-only
 * view that is enough: `on` is absent, so an app that follows changes has
 * nothing to follow, and `change` throws, so an app that tries to write finds
 * out at once rather than losing the edit quietly.
 */
export function snapshotHandle(
  id: string,
  doc: Pick<DocumentDoc, 'path' | 'mime' | 'content'>,
): DocHandle<DocumentDoc> {
  const frozen = { id, workspaceId: '', createdAt: 0, updatedAt: 0, ...doc }
  return {
    documentId: id,
    doc: () => frozen,
    change: () => {
      throw new Error(`${doc.path} is read from a branch and cannot be edited`)
    },
  } as unknown as DocHandle<DocumentDoc>
}

export function App({
  code,
  documentId,
  projectId,
  title,
  snapshot,
  fileDiff,
  onClaimComments,
  onOpenDocument,
  onAppControl,
}: {
  /** The app's built module source, from its view document. */
  code: string
  /** The document to edit. */
  documentId: string
  /** RFC 0277 S8: mount on this instead of finding `documentId` in the repo.
   *  No comments either, since a comment is targeted at a document and a file
   *  off a branch is not one. */
  snapshot?: DocHandle<DocumentDoc>
  fileDiff?: AppFileDiff
  /** Where its comments live: they are project-scoped, like every other
   *  annotation, and targeted at `documentId`. */
  projectId: string
  title: string
  /** Called if the app claims commenting (see `claimComments`). The pane above
   *  uses it to take the host's own commenting surface off this document. */
  onClaimComments?: () => void
  /** RFC 0297 §5a: open a document this one names. Absent means the host has
   *  nowhere to put it, and a link stays text. */
  onOpenDocument?: (id: string, target?: LinkOpenTarget) => void
  /** RFC 0328 3d: this mounted app's controller for `control_app`, and null
   *  when it unmounts. The frame path in `AppView` hands up the same thing. */
  onAppControl?: (controller: AppViewController | null) => void
}) {
  const repo = useRepo() as unknown as Repo
  const held = useOptionalTabRepoBoot()?.held
  // By ref, like the claim: an inline arrow from the pane must not remount.
  const controlRef = useRef(onAppControl)
  controlRef.current = onAppControl
  const containerRef = useRef<HTMLDivElement>(null)
  const [failure, setFailure] = useState<string | null>(null)
  // Through a ref rather than the effect's dependencies. Mounting an app tears
  // down its editor and rebuilds it, so a caller passing an inline arrow would
  // remount the whole document on every render of the pane above.
  const claimRef = useRef(onClaimComments)
  claimRef.current = onClaimComments
  const fileDiffRef = useRef(fileDiff)
  fileDiffRef.current = fileDiff
  // The space's session, by ref for the same reason: it changes whenever the
  // strip's target does, and a comment reads it at the moment of posting.
  const space = useSpaceSession()
  const spaceRef = useRef(space)
  spaceRef.current = space

  // Same ref treatment, and for a second reason: the catalog changes whenever
  // ANY document in the project does, and an editor that remounted on that
  // would throw away the caret every time an agent wrote a file.
  const resolve = useDocumentResolver(projectId)
  // One entry per embed the editor asked for, keyed rather than indexed: the
  // editor creates and destroys these as you scroll and type, and a list keyed
  // by position would re-parent a live app frame every time a line moved.
  const [embeds, setEmbeds] = useState<
    { key: number; el: HTMLElement; id: string }[]
  >([])
  const nextEmbedKey = useRef(0)

  const linksRef = useRef<AppLinks | null>(null)
  linksRef.current = onOpenDocument
    ? {
        resolve: (href) => {
          const subject = resolve(href)
          if (!subject) return null
          return {
            id: subject.id,
            title: subject.title,
            embed: subject.id !== documentId,
          }
        },
        unavailable: (href) => isValidDocumentId(href),
        open: onOpenDocument,
        mount: (el, id) => {
          const key = nextEmbedKey.current++
          setEmbeds((prev) => [...prev, { key, el, id }])
          return () => setEmbeds((prev) => prev.filter((e) => e.key !== key))
        },
      }
    : null

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    let cancelled = false
    let objectUrl: string | null = null
    const disposers: (() => void)[] = []
    // Handed up before the app mounts, as the frame path does: until the app
    // registers, `control_app` gets "does not declare view commands", which is
    // true, rather than "not an app pane", which is not.
    const local = createLocalAppControl()
    controlRef.current?.(local.control)

    const boot = async () => {
      // The app keeps its document and the project for as long as it is
      // mounted, so both are held until then (RFC 0317 §3d).
      if (!snapshot) disposers.push(holdDocument(repo, String(documentId)))
      if (projectId) disposers.push(holdDocument(repo, String(projectId)))
      const handle =
        snapshot ?? (await repo.find<DocumentDoc>(documentId as AnyDocumentId))
      await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
      if (cancelled) return

      /**
       * The project doc, held open for as long as the app is mounted.
       *
       * Comments live beside the document (RFC 0339) and are indexed on the
       * project, and a code file's live on the project's own comments
       * document. Fetched here rather than per call so `list()` can be
       * SYNCHRONOUS: an editor recomputing decorations cannot await, and a
       * promise there would mean a frame drawn with the previous answer.
       *
       * FAILING TO GET IT IS NOT FATAL. An editor that will not open because
       * its comment column could not be reached is a worse editor than one with
       * no commenting: the document is still editable, and that is the part
       * nobody can work around. So this degrades to `undefined`, which the app
       * reads as "this host does not do comments" — the same state every host
       * was in before this existed.
       */
      // A snapshot (a code file, RFC 0277 S13) still takes comments: they are
      // aimed at its code id. Tasks stay a document's.
      const ws = !projectId
        ? null
        : await repo
            .find<ProjectDoc>(projectId as AnyDocumentId)
            .then(async (h) => {
              await (h as { whenReady?: () => Promise<void> }).whenReady?.()
              return h
            })
            .catch(() => null)
      if (cancelled) return

      // This page's comments where they live (RFC 0339), held open beside the
      // project so `list()` stays synchronous.
      const watch = ws ? watchComments(repo, ws, documentId, held) : null
      if (watch) disposers.push(() => watch.stop())

      const comments =
        !ws || !watch
          ? undefined
          : {
              list: () => {
                const doc = ws.doc()
                return projectComments(watch.map(), { documentId }).map(
                  (c) => ({
                    ...c,
                    authorInfo: sessionFace(projectAuthor(doc, c)),
                  }),
                )
              },
              threads: () => projectThreads(watch.map(), { documentId }),
              candidates: (prefix: string) =>
                projectCandidates(ws.doc(), prefix ?? ''),
              create: async (
                anchor: Partial<CommentTarget>,
                body: string,
                mentions?: Mention[],
              ) => {
                const author = await commentAuthor(repo, projectId)
                const id = await createComment(repo, projectId, {
                  // The app names WHERE in the document; the host names the document.
                  // An app cannot address a comment at anything else, which is the
                  // same scoping the bridge enforces for a sandboxed view.
                  target: { ...anchor, documentId },
                  author,
                  body,
                  ...(mentions?.length ? { mentions } : {}),
                })
                // Storing the mention and WAKING what it names are two acts, so a
                // comment that names nobody queues nothing (RFC 0292).
                if (mentions?.length) {
                  await deliverMentions(repo, projectId, {
                    commentId: id,
                    mentions,
                    author,
                    body,
                    documentId,
                    ...(anchor.anchorText
                      ? { anchorText: anchor.anchorText }
                      : {}),
                  })
                } else {
                  // RFC 0301 §3d: naming nobody sends it to the space's session.
                  await routeComment(repo, projectId, spaceRef.current, {
                    id,
                    body,
                    documentId,
                  })
                }
                return id
              },
              // answerComment, not createComment: this is where a person answers
              // an agent, and it wakes the session that wrote the comment being
              // answered (RFC 0289). Written straight to createComment the reply
              // would land correctly and reach nobody, which is the failure the
              // whole path exists to remove.
              reply: async (
                parentId: string,
                body: string,
                mentions?: Mention[],
              ) => {
                const author = await commentAuthor(repo, projectId)
                const out = await answerComment(repo, projectId, {
                  parentId,
                  author,
                  body,
                  ...(mentions?.length ? { mentions } : {}),
                })
                if (mentions?.length) {
                  await deliverMentions(repo, projectId, {
                    commentId: out.id,
                    mentions,
                    author,
                    body,
                    documentId,
                  })
                }
                return { id: out.id, notifiedSessionId: out.notifiedSessionId }
              },
              resolve: (id: string, resolved: boolean) =>
                resolveComment(repo, projectId, id, resolved),
              remove: (id: string) => deleteComment(repo, projectId, id),
              // The same reads the bridge serves a frame (`comments.reader`,
              // `comments.pending`), so a host-realm editor says the same.
              // (target, replyTo), the element's port order, so a host-realm app
              // can hand this object to <CommentThread> as it is.
              reader: (_target: unknown, replyTo?: string) =>
                commentReader({
                  projectDoc: ws.doc(),
                  comments: watch.map(),
                  mentions: [],
                  ...(replyTo ? { replyTo } : {}),
                  space: {
                    sessionId: spaceRef.current.sessionId,
                    canStart: Boolean(spaceRef.current.start),
                  },
                }),
              pending: (rootId: string) => {
                const doc = ws.doc()
                const map = watch.map()
                const thread = projectThreads(map, { documentId }).find(
                  (t) => t.root.id === rootId,
                )
                return thread
                  ? threadWaiting({
                      projectDoc: doc,
                      comments: map,
                      thread,
                      spaceSessionId: spaceRef.current.sessionId,
                    })
                  : null
              },
              subscribe: (fn: () => void) => watch.subscribe(fn),
            }

      const taskLog =
        ws && !snapshot ? new SessionTaskLog(repo, undefined, projectId) : null
      const tasks: AppTasks | undefined =
        !ws || !taskLog
          ? undefined
          : {
              get: (id) => taskLog.get(id),
              create: (input) =>
                taskLog.create(spaceRef.current.sessionId ?? undefined, input),
              update: (id, patch) => taskLog.update(id, patch),
              importChecked: (id, note) =>
                taskLog.importChecked(projectId, id, note),
              subscribe: (fn) => {
                ws.on('change', fn)
                return () => ws.off('change', fn)
              },
            }

      // A blob URL, so the module is loaded by the browser's own module loader
      // and gets a real module scope. `@vite-ignore` because the specifier is a
      // runtime value: Vite would otherwise try to resolve it at build time and
      // fail, which is a build error rather than a missing app.
      objectUrl = URL.createObjectURL(
        new Blob([code], { type: 'text/javascript' }),
      )
      const mod = (await import(/* @vite-ignore */ objectUrl)) as AppModule
      if (cancelled) return

      if (typeof mod.default !== 'function') {
        throw new Error(
          'a host-realm app must export default a mount function; ' +
            `got ${typeof mod.default}`,
        )
      }
      // RFC 0313 3a: every change the person types carries their name, named
      // the way a comment is: the saved person, or the name prompt, filled in
      // from `gh` on the desktop. Asked as the editor opens, not mid-sentence,
      // and read once per mount, which is safe because renaming keeps the id.
      const authorId = await editorAuthor(repo, projectId)
      if (cancelled) return
      mod.default({
        container,
        handle,
        repo,
        automerge: A,
        documentId,
        ...(fileDiffRef.current ? { fileDiff: fileDiffRef.current } : {}),
        comments,
        tasks,
        ...(authorId ? { changeMessage: changeNote({ authorId }) } : {}),
        // NO `edits`, WHICH IS WHAT STOPS THE EDITOR WEDGING (RFC 0309 §3g).
        //
        // `agent-edits.js` called `edits.list()` on every change, debounced by
        // 80ms, and `projectAgentEdits` decodes the whole change history and
        // materialises TWO full document views per agent change. Measured on a
        // 14k-character document: 81ms at 5 agent changes, 129ms at 30, 181ms
        // at 45, all synchronous. Past about twenty the work per tick exceeds
        // the tick, so typing in a document an agent has edited often never
        // lets the main thread drain and the renderer is killed.
        //
        // An editor handed no `edits` mounts no extension, so this line alone
        // removes it. Undo is not lost: it is on the session's edited card, and
        // RFC 0309 S8 gives it a Changes list in the side column.
        // Guarded on `cancelled` like every other effect of booting: a claim
        // arriving from an app whose pane has already gone would set state on a
        // wrapper that is no longer showing this document.
        claimComments: () => {
          if (!cancelled) claimRef.current?.()
        },
        // Through the ref, so the editor keeps working after the catalog moves
        // under it. Same `cancelled` guard: opening a document from an editor
        // whose pane has gone would move a window nobody is looking at.
        links: {
          resolve: (href: string) => linksRef.current?.resolve(href) ?? null,
          unavailable: (href: string) =>
            linksRef.current?.unavailable?.(href) ?? false,
          open: (id: string, target?: LinkOpenTarget) => {
            if (!cancelled) linksRef.current?.open(id, target)
          },
          mount: (el: HTMLElement, id: string) => {
            if (cancelled) return
            return linksRef.current?.mount(el, id)
          },
        },
        control: { register: local.register },
        onDispose: (fn) => disposers.push(fn),
      })
    }

    // A host-realm app runs IN OUR REALM: a throw here would otherwise reach
    // React and blank the whole project, not just this panel. Caught and
    // shown, because the failure that cost the most today was one nothing
    // reported.
    boot().catch((e) => {
      if (!cancelled) setFailure(e instanceof Error ? e.message : String(e))
    })

    return () => {
      cancelled = true
      for (const dispose of disposers) {
        try {
          dispose()
        } catch {
          // One app's bad teardown must not strand the others' or leak the URL.
        }
      }
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      local.close()
      controlRef.current?.(null)
      container.replaceChildren()
    }
  }, [code, documentId, projectId, repo, snapshot, held])

  if (failure) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center p-6">
        <p className="max-w-lg text-center text-destructive text-sm">
          {title} failed to start: {failure}
        </p>
      </div>
    )
  }
  return (
    <>
      <div
        ref={containerRef}
        className="flex min-h-0 flex-1 flex-col"
        onMouseDownCapture={(event) => {
          // Existing editor bundles open a document on `mousedown` but do not
          // pass modifier keys through `links.open`. Catch modified document
          // links at the host boundary so they work without republishing the
          // installed editor. Unmodified clicks remain the editor's to handle.
          if (!event.metaKey || event.button !== 0) return
          const element = (event.target as HTMLElement | null)?.closest?.(
            '.cm-doc-link',
          )
          const id = element?.getAttribute('data-doc-id')
          if (!id) return
          event.preventDefault()
          event.stopPropagation()
          onOpenDocument?.(id, event.shiftKey ? 'space' : 'split')
        }}
      />
      {/* PORTALS, not a React root per embed. The editor's widgets live in DOM
          this component does not own, and mounting a second root into each one
          would cut them off from every context above: the repo, the theme, the
          embed-depth guard. A portal keeps them in this tree while the nodes
          stay where CodeMirror put them. */}
      {embeds.map((e) =>
        createPortal(
          <Embed id={e.id} projectId={projectId} onOpen={onOpenDocument} />,
          e.el,
          String(e.key),
        ),
      )}
    </>
  )
}

/**
 * One embed the editor asked for, drawn as the read mode draws it.
 *
 * The SAME component, so the two engines cannot disagree about what a document
 * link on its own line looks like. That was the point of doing this twice.
 */
function Embed({
  id,
  projectId,
  onOpen,
}: {
  id: string
  projectId: string
  onOpen?: (id: string, target?: LinkOpenTarget) => void
}) {
  const resolve = useDocumentResolver(projectId)
  const subject = resolve(id)
  // The catalog may not have arrived yet, or the document may have gone since
  // the editor asked. Nothing is the right answer to both: the link above it is
  // still there and still says what it names.
  if (!subject) return null
  return (
    <DocumentEmbed subject={subject} projectId={projectId} onOpen={onOpen} />
  )
}
